import React, { useState } from 'react';
import { motion } from 'framer-motion';
import 'tailwindcss/tailwind.css';
import BackgroundImage from "../../../../../images/stupid-comedy-bg2.jpg"

export const FAQSection = () => {
  // Variantes de animación para la sección completa
  const sectionVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  // Variantes para el título
  const titleVariants = {
    hidden: { y: -50, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: "spring",
        stiffness: 100,
        duration: 0.8
      }
    }
  };

  return (
  <motion.section 
    className="py-16 md:py-24 text-white relative"
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.2 }}
    variants={sectionVariants}
    style={{
      backgroundImage: `url(${BackgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }}
  >
    {/* Overlay oscuro para mejorar la legibilidad del texto */}
    <div className="absolute inset-0 bg-black bg-opacity-70"></div>
    
    {/* El resto del contenido debe tener position relative para aparecer sobre el overlay */}
    <div className="relative z-10">
      {/* Elemento decorativo animado */}
      <motion.div
        className="absolute top-0 left-0 w-full h-24 opacity-10 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, rgba(255,87,34,0.3) 0%, rgba(0,0,0,0) 100%)'
        }}
        initial={{ scaleY: 0, opacity: 0 }}
        whileInView={{ 
          scaleY: 1, 
          opacity: 0.1,
          transition: { duration: 1.2 }
        }}
        viewport={{ once: true }}
      />
      
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.h2 
          className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8 md:mb-12"
          variants={titleVariants}
        >
          Preguntas frecuentes
        </motion.h2>
        
        <div className="space-y-4 md:space-y-6">
          <FAQItem
            question="¿Qué es Stupid Comedy?"
            answer="Es una plataforma de streaming con rutinas de stand-up, sketches y contenido de comedia independiente. Ofrecemos una experiencia única con el mejor contenido de humor."
            index={0}
          />
          <FAQItem
            question="¿Cuánto cuesta la suscripción?"
            answer="Estamos definiendo los detalles finales de nuestros planes de suscripción. Pronto anunciaremos las opciones disponibles con todos sus beneficios. ¡Mantente atento a nuestras redes sociales para ser el primero en enterarte!"
            index={1}
          />
          <FAQItem
            question="¿Puedo ver Stupid Comedy desde mi celular?"
            answer="¡Por supuesto! Puedes disfrutar de nuestro contenido desde cualquier dispositivo con conexión a internet utilizando tu navegador web. Simplemente visita nuestra página desde tu smartphone o tablet para comenzar a reír."
            index={2}
          />
          <FAQItem
            question="¿Ofrecen transmisiones en vivo?"
            answer="Sí, realizamos livestreams de shows de stand-up comedy. Podrás disfrutar de presentaciones en vivo desde la comodidad de tu hogar. Consulta nuestra programación para conocer las fechas de los próximos eventos."
            index={3}
          />
          <FAQItem
            question="¿Puedo comprar entradas para shows presenciales?"
            answer="¡Definitivamente! A través de nuestra plataforma podrás adquirir entradas para shows presenciales de comedia. Ofrecemos acceso preferencial y descuentos exclusivos para nuestros suscriptores."
            index={4}
          />
          <FAQItem
            question="¿Dónde está disponible el servicio?"
            answer="Actualmente, Stupid Comedy está disponible exclusivamente en Chile. Estamos trabajando para expandirnos a más países en el futuro."
            index={5}
          />
        </div>

      </div>
    </div>
    </motion.section>
  );
};

const FAQItem = ({ question, answer, index }) => {
  // Estado para controlar si el FAQ está abierto
  const [isOpen, setIsOpen] = useState(false);

  // Variantes de animación para cada item
  const itemVariants = {
    hidden: { 
      opacity: 0,
      x: -50
    },
    visible: { 
      opacity: 1,
      x: 0,
      transition: { 
        type: "spring",
        stiffness: 80,
        delay: index * 0.1
      }
    }
  };

  // Variantes para el contenido desplegable
  const contentVariants = {
    collapsed: { 
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.4,
        ease: "easeInOut"
      }
    },
    expanded: { 
      height: "auto",
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeInOut"
      }
    }
  };

  return (
    <motion.div
      className="bg-zinc-900 hover:bg-zinc-800 p-4 md:p-6 rounded-xl transition-all duration-300"
      variants={itemVariants}
      onClick={() => setIsOpen(!isOpen)}
      whileHover={{ 
        boxShadow: "0 0 15px rgba(255, 87, 34, 0.3)",
        scale: 1.01,
        transition: { duration: 0.3 }
      }}
    >
      <div className="cursor-pointer text-base md:text-xl font-semibold flex items-center justify-between">
        <motion.span
          initial={{ scale: 1 }}
          whileHover={{ scale: 1.02 }}
        >
          {question}
        </motion.span>
        <motion.span 
          className="ml-4 md:ml-6 flex-shrink-0"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.4 }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="md:w-6 md:h-6">
            <path d="M6 9L12 15L18 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </motion.span>
      </div>

      <motion.div
        variants={contentVariants}
        initial="collapsed"
        animate={isOpen ? "expanded" : "collapsed"}
        className="overflow-hidden"
      >
        <motion.p 
          className="mt-3 md:mt-4 text-zinc-300 text-sm md:text-base leading-relaxed"
          initial={{ opacity: 0 }}
          animate={isOpen ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          {answer}
        </motion.p>
      </motion.div>
    </motion.div>
  );
};